import React, { useState, useRef, useEffect } from 'react';
import { Badge } from 'antd';
import { getFontClass } from '../util';

/**
 * Props for MenuItem component
 */
interface MenuItemProps {
  /** Icon element to display */
  icon: React.ReactElement;
  /** Label text for the menu item */
  label?: string;
  /** Title text (alias for label for backward compatibility) */
  title?: any;
  /** Whether this menu item is currently active */
  active?: boolean;
  /** Current location for active state checking */
  current?: string;
  /** Location path to compare with current */
  location?: string;
  /** Whether the sidebar is collapsed (icon only mode) */
  collapsed?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Navigation function (alias for onClick for backward compatibility) */
  to?: Function;
  /** Whether the menu item is disabled */
  disabled?: boolean;
  /** Whether to display the menu item */
  display?: boolean;
  /** Badge count for notifications */
  badge?: number;
  /** Whether to show the badge as a dot */
  badgeDot?: boolean;
  /** Optional className for custom styling */
  className?: string;
}

/**
 * MenuItem Component
 * 
 * A modern, accessible menu item component with:
 * - Active, hover, and focus states
 * - Badge/notification indicators
 * - Collapsed state (icon only)
 * - 44px minimum touch target
 * - Keyboard navigation support
 * - Smooth animations
 * 
 * @example
 * ```tsx
 * <MenuItem
 *   icon={<DashboardIcon />}
 *   label="Dashboard"
 *   active={true}
 *   badge={5}
 *   onClick={() => navigate('/dashboard')}
 * />
 * ```
 */
const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  label,
  title,
  active = false,
  current,
  location,
  collapsed = false,
  onClick,
  to,
  disabled = false,
  display = true,
  badge,
  badgeDot = false,
  className = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Support both label and title props for backward compatibility
  const displayLabel = label || title;
  
  // Support both active prop and current/location comparison
  const isActive = active || (location && current === location);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    
    // Activate on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
      to?.();
    }
  };

  // Handle click
  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    to?.();
  };
  
  // Don't render if display is false
  if (!display) return null;

  // Ensure focus is visible for keyboard navigation
  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const handleFocusIn = () => setIsFocused(true);
    const handleFocusOut = () => setIsFocused(false);

    button.addEventListener('focusin', handleFocusIn);
    button.addEventListener('focusout', handleFocusOut);

    return () => {
      button.removeEventListener('focusin', handleFocusIn);
      button.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      aria-label={displayLabel}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      className={`
        group relative w-full
        flex items-center gap-3
        min-h-[44px] px-3 py-3 mt-2
        rounded-xl
        transition-all duration-300 ease-in-out
        transform
        ${
          isActive
            ? 'bg-white shadow-lg shadow-white/10 scale-[1.02]'
            : disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-white/10 hover:scale-[1.01] cursor-pointer backdrop-blur-sm'
        }
        ${isFocused && !disabled ? 'ring-2 ring-white ring-offset-2 ring-offset-neo-primary' : ''}
        ${className}
      `}
      style={{
        scrollMarginTop: '20px',
        scrollMarginBottom: '20px'
      }}
    >
      {/* Icon Container */}
      <div
        className={`
          relative flex-shrink-0
          w-10 h-10
          flex items-center justify-center
          rounded-lg
          transition-all duration-300 ease-in-out
          transform
          ${
            isActive
              ? 'bg-neo-primary text-white shadow-md scale-110'
              : 'bg-white/10 text-white/90 shadow-sm'
          }
          ${isHovered && !isActive && !disabled ? 'bg-white/20 text-white scale-110 rotate-3' : ''}
        `}
      >
        {/* Badge for icon */}
        {(badge !== undefined || badgeDot) && !collapsed ? (
          <Badge
            count={badgeDot ? 0 : badge}
            dot={badgeDot}
            offset={[-2, 2]}
            className="flex items-center justify-center"
          >
            <div className="text-xl">{icon}</div>
          </Badge>
        ) : (
          <div className="text-xl">{icon}</div>
        )}
      </div>

      {/* Label (hidden when collapsed) */}
      {!collapsed && (
        <span
          className={`
            flex-1 text-left
            text-sm font-medium
            transition-all duration-300 ease-in-out
            transform
            ${isActive ? 'text-neo-primary font-semibold translate-x-1' : 'text-white/90'}
            ${isHovered && !isActive && !disabled ? 'translate-x-1' : ''}
            ${getFontClass('', 400)}
          `}
        >
          {displayLabel}
        </span>
      )}

      {/* Badge for label (only shown when not collapsed and no icon badge) */}
      {!collapsed && badge !== undefined && !badgeDot && (
        <Badge
          count={badge}
          showZero={false}
          className="flex-shrink-0"
          style={{
            backgroundColor: isActive ? '#441752' : 'rgba(255, 255, 255, 0.2)',
            color: isActive ? 'white' : 'white',
          }}
        />
      )}

      {/* Active Indicator */}
      {isActive && !collapsed && (
        <div 
          className="flex-shrink-0 w-1 h-6 bg-neo-primary rounded-full opacity-90 animate-pulse"
          style={{
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}
        />
      )}

      {/* Tooltip for collapsed state */}
      {collapsed && (
        <div
          className={`
            absolute left-full ml-2 px-3 py-2
            bg-gray-900 text-white text-sm font-medium
            rounded-lg shadow-lg
            whitespace-nowrap
            pointer-events-none
            transition-opacity duration-200
            ${isHovered ? 'opacity-100' : 'opacity-0'}
            z-50
          `}
          role="tooltip"
        >
          {displayLabel}
          {badge !== undefined && badge > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-white text-neo-primary rounded-full text-xs font-semibold">
              {badge}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

export default MenuItem;
export type { MenuItemProps };
